import { Box, Flex, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';

import { playStoreUrl } from '#features/app-install';
import { AppleGlyph, GooglePlayGlyph } from '../storeGlyphs';

// A one-line hint under the hero CTAs: the app exists, without competing with the
// sign-up button. Deliberately small — full-size store badges in the hero belong to
// app-first products; here the primary action is still "create an account".
type StorePillProps = {
  glyph: React.ReactNode;
  name: string;
  suffix?: string;
  href?: string;
};

const StorePill = ({ glyph, name, suffix, href }: StorePillProps) => {
  const available = Boolean(href);
  return (
    <Flex
      as={available ? 'a' : 'div'}
      {...(available ? { href, target: '_blank', rel: 'noopener noreferrer' } : {})}
      alignItems="center"
      gap={1.5}
      px={2.5}
      h="28px"
      borderRadius="full"
      border="1px solid"
      borderColor="whiteAlpha.200"
      bg="whiteAlpha.50"
      opacity={available ? 1 : 0.55}
      cursor={available ? 'pointer' : 'default'}
      transition="all 0.2s"
      _hover={available ? { borderColor: 'whiteAlpha.400', bg: 'whiteAlpha.100' } : undefined}
    >
      <Box color="fg.emphasized" flexShrink={0} display="flex">
        {glyph}
      </Box>
      <Text fontSize="xs" fontWeight="700" color="fg.emphasized" whiteSpace="nowrap">
        {name}
      </Text>
      {suffix && (
        <Text fontSize="2xs" color="fg.muted" whiteSpace="nowrap">
          {suffix}
        </Text>
      )}
    </Flex>
  );
};

export const StoreNote = () => {
  const { t } = useLingui();

  return (
    <Flex
      alignItems="center"
      gap={2}
      flexWrap="wrap"
      justifyContent={{ base: 'center', lg: 'flex-start' }}
    >
      <Text
        fontSize="2xs"
        color="fg.muted"
        textTransform="uppercase"
        letterSpacing="0.08em"
        fontWeight="600"
      >
        {t`v3.hero.alsoOn`}
      </Text>
      <StorePill glyph={<GooglePlayGlyph size={14} />} name="Google Play" href={playStoreUrl('landing-hero')} />
      <StorePill glyph={<AppleGlyph size={14} />} name="App Store" suffix={t`v3.hero.storeSoon`} />
    </Flex>
  );
};
